import React, { useEffect, useRef } from "react";

/*
 * AuroraBackground — a multi-layered, continuously animated background.
 * Stays behind all content (z-index -10) so it NEVER overlaps text.
 *
 * Layers:
 *   1. Base off-white gradient
 *   2. Mouse-parallax drifting maroon/accent blobs (slow morph)
 *   3. Canvas: floating dust particles drifting upward
 *   4. SVG: animated wavy curves at top & bottom
 *   5. Dot pattern + slowly rotating conic accent
 */
export default function AuroraBackground() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  // Mouse parallax for blobs
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let raf = 0;
    let tx = 0, ty = 0, cx = 0, cy = 0;
    const onMove = (e) => {
      const w = window.innerWidth, h = window.innerHeight;
      tx = (e.clientX / w - 0.5) * 30;
      ty = (e.clientY / h - 0.5) * 30;
    };
    const tick = () => {
      cx += (tx - cx) * 0.05;
      cy += (ty - cy) * 0.05;
      el.style.setProperty("--mx", cx + "px");
      el.style.setProperty("--my", cy + "px");
      raf = requestAnimationFrame(tick);
    };
    tick();
    window.addEventListener("mousemove", onMove);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("mousemove", onMove); };
  }, []);

  // Floating particles canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    let raf = 0;
    let w = 0, h = 0;
    let particles = [];
    const COUNT = 38;

    const seed = () => {
      particles = Array.from({ length: COUNT }).map(() => ({
        x: Math.random() * w,
        y: Math.random() * h,
        size: 0.6 + Math.random() * 1.6,
        vy: 0.12 + Math.random() * 0.35,
        vx: (Math.random() - 0.5) * 0.12,
        op: 0.18 + Math.random() * 0.45,
        hue: Math.random() > 0.65 ? "#98413F" : "#73100D",
      }));
    };

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.y -= p.vy;
        p.x += p.vx;
        if (p.y < -6) { p.y = h + 6; p.x = Math.random() * w; }
        if (p.x < -6) p.x = w + 6;
        if (p.x > w + 6) p.x = -6;
        // soft halo
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 4);
        g.addColorStop(0, p.hue + "55");
        g.addColorStop(1, p.hue + "00");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 4, 0, Math.PI * 2);
        ctx.fill();
        // core
        ctx.fillStyle = `${p.hue}${Math.round(p.op * 255).toString(16).padStart(2, "0")}`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="fixed inset-0 -z-10 overflow-hidden pointer-events-none"
      style={{ "--mx": "0px", "--my": "0px" }}
    >
      {/* Base */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-[#fcfafa] to-white" />

      {/* Drifting morphing blobs */}
      <div
        className="absolute aurora-morph rounded-full blur-3xl opacity-[0.16]"
        style={{
          width: "720px", height: "720px",
          left: "-12%", top: "-8%",
          background: "radial-gradient(circle, #73100D, transparent 65%)",
          transform: "translate3d(var(--mx), var(--my), 0)",
        }}
      />
      <div
        className="absolute aurora-morph-2 rounded-full blur-3xl opacity-[0.13]"
        style={{
          width: "640px", height: "640px",
          right: "-10%", top: "35%",
          background: "radial-gradient(circle, #98413F, transparent 60%)",
          transform: "translate3d(calc(var(--mx)*-1), calc(var(--my)*-1), 0)",
        }}
      />
      <div
        className="absolute aurora-morph-3 rounded-full blur-3xl opacity-[0.10]"
        style={{
          width: "520px", height: "520px",
          left: "22%", bottom: "-12%",
          background: "radial-gradient(circle, #5E0000, transparent 60%)",
          transform: "translate3d(var(--mx), calc(var(--my)*-1), 0)",
        }}
      />

      {/* Slowly rotating conic accent (very faint) */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[140vmax] h-[140vmax] opacity-[0.04] conic-rotate"
        style={{
          background: "conic-gradient(from 0deg at 50% 50%, #73100D 0deg, transparent 60deg, transparent 300deg, #98413F 360deg)",
        }}
      />

      {/* Dot pattern */}
      <div className="absolute inset-0 dot-pattern opacity-[0.45]" />

      {/* Animated wavy curves top/bottom */}
      <svg className="absolute top-0 left-0 w-full h-[180px] opacity-[0.10]" viewBox="0 0 1440 180" preserveAspectRatio="none" fill="none">
        <path className="wave-path-a" d="M0 80 Q 360 20 720 80 T 1440 80 V0 H0 Z" fill="#73100D" />
      </svg>
      <svg className="absolute bottom-0 left-0 w-full h-[180px] opacity-[0.08]" viewBox="0 0 1440 180" preserveAspectRatio="none" fill="none">
        <path className="wave-path-b" d="M0 100 Q 360 160 720 100 T 1440 100 V180 H0 Z" fill="#98413F" />
      </svg>

      {/* Floating particles canvas */}
      <canvas ref={canvasRef} className="absolute inset-0" />

      {/* Vignette to keep edges soft */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_55%,_rgba(255,255,255,0.55)_100%)]" />
    </div>
  );
}
